/**
 * Icon Mapping Constants
 * Maps menu items to their corresponding icon files from figmaAssets/
 * 
 * Icon sources migrated from WATLOG-main/client/public/figmaAssets/
 * Total icons available: 15 SVG files
 */

// Base path for all icons
export const ICON_BASE_PATH = '/figmaAssets';

/**
 * Icon file mappings
 * Each key represents a functional area or menu item
 */
export const ICONS = {
  // Analysis Tools Section
  CLAIM_ANALYSIS: `${ICON_BASE_PATH}/icon-19.svg`,         // Dashboard/Stats icon
  TEST_PAGE: `${ICON_BASE_PATH}/icon-3.svg`,               // Product management/Testing icon
  LABEL_MANAGER: `${ICON_BASE_PATH}/icon-5.svg`,           // Labeling review icon
  REPORTS: `${ICON_BASE_PATH}/icon-14.svg`,                // Partner sharing/Reports icon
  
  // Admin Tools Section
  TEST_RUNNER: `${ICON_BASE_PATH}/icon-3.svg`,             // Sys-Admin Testing icon
  WARNING_LETTERS: `${ICON_BASE_PATH}/icon-15.svg`,        // FAQ/Help icon
  DATA_EXPLORER: `${ICON_BASE_PATH}/icon-13.svg`,          // Data management icon
  USERS: `${ICON_BASE_PATH}/icon-10.svg`,                  // Multi-purpose analysis icon
  AUDIT_LOG: `${ICON_BASE_PATH}/icon-12.svg`,              // Audit/Security logs icon
  PROFILES: `${ICON_BASE_PATH}/icon-6.svg`,                // Settings/Config icon
  
  // Account Section
  CREDITS: `${ICON_BASE_PATH}/icon-4.svg`,                 // Credits/Payment icon
  SETTINGS: `${ICON_BASE_PATH}/icon-6.svg`,                // Settings/Config icon
  FAQ: `${ICON_BASE_PATH}/icon-15.svg`,                    // FAQ/Help icon
  FEEDBACK: `${ICON_BASE_PATH}/icon-7.svg`,                // Feedback/Support icon
  ABOUT: `${ICON_BASE_PATH}/icon-2.svg`,                   // About/Info icon
  
  // Reserved/Future Use
  GENERIC: `${ICON_BASE_PATH}/icon.svg`,                   // Generic fallback icon
  MARKETING_AI: `${ICON_BASE_PATH}/icon-9.svg`,            // Marketing AI icon
  RESERVED_2: `${ICON_BASE_PATH}/icon-17.svg`,             // Reserved for future use
};

/**
 * Menu item configurations with their corresponding icons
 * Used by Sidebar component to render navigation items
 */
export const MENU_ITEMS = {
  // Analysis Tools Section (📊)
  analysis: [
    {
      id: 'claim-analysis',
      label: 'Claim Analysis',
      path: '/app',
      icon: ICONS.CLAIM_ANALYSIS,
      description: 'FDA compliance analysis',
      permission: 'access:analysis', // Available to all authenticated users
    },
    {
      id: 'reports',
      label: 'Reports',
      path: '/reports',
      icon: ICONS.REPORTS,
      description: 'View analysis reports',
      permission: 'access:my_reports',
    },
  ],
  
  // Admin Tools Section (🔧)
  admin: [
    {
      id: 'test-runner',
      label: 'Test Runner',
      path: '/admin',
      icon: ICONS.TEST_RUNNER,
      description: 'Run system tests',
      permission: 'access:test_runner',
      badge: 'Admin',
    },
    {
      id: 'data-explorer',
      label: 'Data Explorer',
      path: '/admin/data-explorer',
      icon: ICONS.DATA_EXPLORER,
      description: 'Explore system data (incl. the Warning Letters pipeline tab)',
      permission: 'access:data_explorer',
    },
    {
      id: 'site-users',
      label: 'Site Admin User Manager',
      path: '/admin/site-users',
      icon: ICONS.USERS,
      description: 'Manage all users across all organizations',
      permission: null,
      siteAdminOnly: true,
      badge: 'Root',
    },
    {
      id: 'users',
      label: 'User Management',
      path: '/admin/users',
      icon: ICONS.USERS,
      description: 'Manage your organization users',
      permission: 'access:user_management',
      orgAdminOnly: true,
      badge: 'Admin',
    },
    {
      id: 'manual-payments',
      label: 'Manual Payments',
      path: '/admin/payments',
      icon: ICONS.CREDITS,
      description: 'Manage manual payments and memberships',
      permission: 'access:payment_management',
      badge: 'Admin',
    },
    {
      id: 'gold-dataset',
      label: 'Gold Dataset',
      path: '/admin/gold',
      icon: ICONS.DATA_EXPLORER,
      description: 'Adjudicate classification gold for AI/ML training',
      permission: 'access:gold_dataset',
      badge: 'Root',
    },
    {
      id: 'audit-log',
      label: 'Audit Log',
      path: '/admin/audit',
      icon: ICONS.AUDIT_LOG,
      description: 'System audit trail',
      permission: 'access:audit_log',
      badge: 'Root',
    },
    {
      id: 'sessions',
      label: 'Sessions',
      path: '/admin/sessions',
      icon: ICONS.AUDIT_LOG, // Using audit icon for now (security-related)
      description: 'Manage user sessions',
      permission: 'admin:sessions',
      badge: 'Admin',
    },
    {
      id: 'email-manager',
      label: 'Email Service',
      path: '/admin/email',
      icon: ICONS.RESERVED_2,
      description: 'Email service management',
      permission: 'access:user_management',
      siteAdminOnly: true,
      badge: 'Admin',
    },
    {
      id: 'profiles',
      label: 'Site Admin Manager',
      path: '/admin/profiles',
      icon: ICONS.PROFILES,
      description: 'Manage root users and site admins',
      permission: null,
      siteAdminOnly: true,
      badge: 'Root',
    },
    {
      id: 'pipeline-health',
      label: 'Pipeline Health',
      path: '/admin/pipeline',
      icon: ICONS.AUDIT_LOG,
      description: 'Pipeline operational status',
      permission: 'access:test_runner',
      badge: 'Admin',
    },
    {
      id: 'marketing-ai',
      label: 'Marketing AI',
      path: '/admin/marketing-ai',
      icon: ICONS.MARKETING_AI,
      description: 'Generate compliant marketing copy with AI',
      permission: null,
      siteAdminOnly: true,
      badge: 'Root',
    },
    {
      id: 'promotions',
      label: 'Promotion Manager',
      path: '/admin/promotions',
      icon: ICONS.CREDITS,
      description: 'Create and track discount coupon campaigns',
      permission: null,
      siteAdminOnly: true,
      badge: 'Root',
    },
  ],
  
  // Account Section (💰)
  account: [
    {
      id: 'settings',
      label: 'Settings',
      path: '/settings',
      icon: ICONS.SETTINGS,
      description: 'Account settings',
      permission: null,
    },
  ],
};

/**
 * Helper function to get icon path by key
 * @param {string} key - Icon key from ICONS object
 * @returns {string} Full path to icon file
 */
export const getIcon = (key) => {
  return ICONS[key] || ICONS.GENERIC;
};

/**
 * Helper function to get menu items by section
 * @param {string} section - Section name ('analysis', 'admin', 'account')
 * @returns {Array} Array of menu item objects
 */
export const getMenuItems = (section) => {
  return MENU_ITEMS[section] || [];
};

/**
 * Helper function to check if a menu item should be visible
 * @param {Object} item - Menu item object
 * @param {Function} hasPermission - Permission check function from AuthContext
 * @returns {boolean} Whether the item should be visible
 */
export const shouldShowMenuItem = (item, hasPermission, isSiteAdmin = false) => {
  // Site-admin-only items are hidden from org-admins and regular users
  if (item.siteAdminOnly && !isSiteAdmin) {
    return false;
  }

  // Org-admin-only items are hidden from site admins (they have their own page)
  if (item.orgAdminOnly && isSiteAdmin) {
    return false;
  }

  // If no permission required, always show
  if (!item.permission) {
    return true;
  }
  
  // Check if user has required permission
  return hasPermission(item.permission);
};

export default ICONS;
